// Default imports
import React from "react";
import { Container } from "react-bootstrap";

// Import GraphQl data
import { footerTopData } from "../data/options-footer-top.data";

// Import SVG
import Logo from "../images/white-logo-cropped.svg";

// Order of the nav blocks when the columns collapse on mobile
const MOBILE_NAV_ORDER = [
    "solutions",
    "segments",
    "product",
    "connectivity",
    "news and insights",
    "company",
    "resources",
    "legal",
];

const blockTitle = (block) => block.headerLink?.title || block.title || "";

const mobileOrder = (block) => {
    const index = MOBILE_NAV_ORDER.indexOf(
        blockTitle(block).trim().toLowerCase()
    );
    return index === -1 ? MOBILE_NAV_ORDER.length : index;
};

// "Twitter / X" -> "X": the footer lists networks by their short name
const socialLabel = (title) => title?.split("/").pop().trim();

const handleBlankLinkClick = (event, link) => {
    if (link?.target !== "_blank" || !link?.url) return;

    event.preventDefault();
    event.nativeEvent?.stopImmediatePropagation?.();
    window.open(link.url, "_blank");
};

const FooterBlock = ({ block }) => {
    const links = block.links?.filter(({ link }) => link?.url) || [];

    return (
        <div className="footer__group">
            <h6 className="footer__group-header">
                {block.headerLink?.url ? (
                    <a
                        href={block.headerLink.url}
                        target={block.headerLink.target}
                        onClickCapture={(event) =>
                            handleBlankLinkClick(event, block.headerLink)
                        }
                    >
                        {blockTitle(block)}
                    </a>
                ) : (
                    block.title
                )}
            </h6>

            {links.length > 0 && (
                <ul className="footer__links">
                    {links.map(({ link }, index) => (
                        <li key={index}>
                            <a
                                href={link.url}
                                target={link.target}
                                className="footer__link"
                                onClickCapture={(event) =>
                                    handleBlankLinkClick(event, link)
                                }
                            >
                                {link.title}
                            </a>
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
};

const FooterContact = ({ title, contactInfo }) => (
    <div className="footer__contact">
        {title && <h6 className="footer__group-header">{title}</h6>}
        {contactInfo.address && (
            <p
                className="footer__address"
                dangerouslySetInnerHTML={{
                    __html: contactInfo.address.replace(/(\n|\\n|\r)+/g, ""),
                }}
            />
        )}
        {(contactInfo.phone || contactInfo.email) && (
            <p className="footer__address">
                {contactInfo.phone && (
                    <a href={`tel:${contactInfo.phone.replace(/\s+/g, "")}`}>
                        {contactInfo.phone}
                    </a>
                )}
                {contactInfo.phone && contactInfo.email && <br />}
                {contactInfo.email && (
                    <a href={`mailto:${contactInfo.email}`}>
                        {contactInfo.email}
                    </a>
                )}
            </p>
        )}
    </div>
);

const Footer = () => {
    const themeSettings = footerTopData() || {};
    const socials = themeSettings.socialMedia || [];
    const {
        tagline,
        certifications,
        copyright,
        columns = [],
    } = themeSettings.footer || {};

    const navColumns = columns
        .map((col) => (col.blocks || []).filter((b) => b.type !== "contact"))
        .filter((blocks) => blocks.length > 0);
    const contactBlock = columns
        .flatMap((col) => col.blocks || [])
        .find((b) => b.type === "contact" && b.contactInfo);
    const mobileBlocks = navColumns
        .flat()
        .map((block, index) => ({ block, index }))
        .sort(
            (a, b) =>
                mobileOrder(a.block) - mobileOrder(b.block) || a.index - b.index
        )
        .map(({ block }) => block);

    // One paragraph per line, as entered in the theme settings
    const certLines = (certifications || "")
        .split(/<br\s*\/?>|\r?\n|\\n/i)
        .map((line) => line.trim())
        .filter(Boolean);

    const currentYear = new Date().getFullYear();
    const displayCopyright = copyright
        ? copyright.replace("{{year}}", currentYear)
        : `© ${currentYear} Wyden. All Rights Reserved.`;

    const copyrightLine = (modifier) => (
        <p className={`footer__copyright footer__copyright--${modifier}`}>
            {displayCopyright}
        </p>
    );

    return (
        <footer className="footer">
            <Container>
                <div className="footer__grid">
                    <div className="footer__brand">
                        <Logo className="footer__logo" />

                        {tagline && (
                            <p
                                className="footer__tagline"
                                dangerouslySetInnerHTML={{
                                    __html: tagline.replace(
                                        /(\n|\\n)+/g,
                                        "<br/>"
                                    ),
                                }}
                            />
                        )}

                        {certLines.map((line, index) => (
                            <p
                                key={index}
                                className="footer__cert-text"
                                dangerouslySetInnerHTML={{ __html: line }}
                            />
                        ))}

                        {socials.length > 0 && (
                            <div className="footer__socials">
                                {socials.map(({ link }) =>
                                    link?.url ? (
                                        <a
                                            key={link.url}
                                            href={link.url}
                                            title={link.title}
                                            target={link.target}
                                            className="footer__social"
                                            onClickCapture={(event) =>
                                                handleBlankLinkClick(
                                                    event,
                                                    link
                                                )
                                            }
                                        >
                                            {socialLabel(link.title)}
                                        </a>
                                    ) : null
                                )}
                            </div>
                        )}

                        {contactBlock && (
                            <div className="footer__contact-wrap footer__contact-wrap--desktop">
                                <FooterContact {...contactBlock} />
                            </div>
                        )}

                        {copyrightLine("desktop")}
                    </div>

                    {navColumns.map((blocks, colIndex) => (
                        <div
                            key={colIndex}
                            className="footer__column footer__column--desktop"
                        >
                            {blocks.map((block, blockIndex) => (
                                <FooterBlock key={blockIndex} block={block} />
                            ))}
                        </div>
                    ))}

                    <div className="footer__mobile-nav">
                        {mobileBlocks.map((block, blockIndex) => (
                            <FooterBlock key={blockIndex} block={block} />
                        ))}
                    </div>

                    {contactBlock && (
                        <div className="footer__contact-wrap footer__contact-wrap--mobile">
                            <FooterContact {...contactBlock} />
                        </div>
                    )}

                    {copyrightLine("mobile")}
                </div>
            </Container>
        </footer>
    );
};

export default Footer;
